import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { ProcessedReport } from '@integratio/core';
import { FullReportPreview, type PrintChart } from '../components/FullReportPreview';
import { ApiError, api } from '../lib/api';

const VALID_CHARTS: PrintChart[] = ['contexto', 'prioridade', 'postura', 'poder-interesses', 'tabela'];

/**
 * Rota de impressão consumida pelo usuário (pré-visualização) e pelo
 * renderizador Playwright. Sinaliza "data-print-ready" quando o layout está pronto.
 */
export function PrintPage(): JSX.Element {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const chartParam = searchParams.get('chart');
  const chart = VALID_CHARTS.find((item) => item === chartParam);

  const [report, setReport] = useState<ProcessedReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!reportId) return;
    let active = true;
    void api
      .getReport(reportId)
      .then((data) => {
        if (active) setReport(data);
      })
      .catch((cause) => {
        if (active) {
          setError(cause instanceof ApiError ? cause.message : 'Não foi possível carregar o relatório.');
        }
      });
    return () => {
      active = false;
    };
  }, [reportId]);

  useEffect(() => {
    if (!report) return;
    let cancelled = false;
    const markReady = (): void => {
      if (!cancelled) {
        // Dois frames + fontes carregadas garantem que o layout está estável.
        requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
      }
    };
    if (document.fonts?.ready) {
      void document.fonts.ready.then(markReady);
    } else {
      markReady();
    }
    return () => {
      cancelled = true;
    };
  }, [report]);

  if (error) {
    return (
      <div className="p-10">
        <p className="text-sm text-brand-redDark">{error}</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-10">
        <p className="text-sm text-brand-slate">Carregando relatório…</p>
      </div>
    );
  }

  return (
    <div data-print-ready={ready ? 'true' : 'false'} className="bg-brand-mist py-6 print:bg-white print:py-0">
      <div className="mx-auto w-fit space-y-6 print:m-0 print:w-full print:space-y-0">
        <FullReportPreview report={report} chart={chart} />
      </div>
    </div>
  );
}
